import { useRef, useState } from "react";
import { Upload, Trash2, ChevronUp, ChevronDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Quote, Attachment } from "@/lib/types";
import { uploadFile, errMsg, assetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props { quote: Quote; update: (patch: Partial<Quote>) => void; appendixTypes: any[]; editable: boolean; onSelect: (id: string) => void }

export function FilesTab({ quote, update, appendixTypes, editable, onSelect }: Props) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const atts = quote.attachments;
  const set = (attachments: Attachment[]) => update({ attachments });
  const patch = (id: string, p: Partial<Attachment>) => set(atts.map((a) => a.id === id ? { ...a, ...p } : a));

  const upload = async (f: File) => {
    setBusy(true);
    try {
      const r = await uploadFile(f, "imported-pdfs");
      const a: Attachment = { id: crypto.randomUUID(), file_id: r.id, url: r.url, title: f.name.replace(/\.pdf$/i, ""), mode: "appendix", position: quote.pages.length - 1, sort_order: atts.length, appendix_type_id: null, description: "", original_filename: f.name };
      set([...atts, a]); onSelect(`att:${a.id}`); toast.success("PDF imported — give it a title and choose section vs appendix");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const move = (i: number, dir: number) => {
    const j = i + dir; if (j < 0 || j >= atts.length) return;
    const next = [...atts]; [next[i], next[j]] = [next[j], next[i]]; set(next.map((a, k) => ({ ...a, sort_order: k })));
  };

  return (
    <div className="space-y-4" data-testid="files-tab">
      <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center">
        <p className="text-xs text-gray-600">Import third-party PDFs (roof measurements, site plans, spec sheets). Rename them, then render inline as a section or attach as a named appendix.</p>
        <Button size="sm" className="mt-3 bg-blue-700 hover:bg-blue-800" disabled={!editable || busy} data-testid="import-pdf-btn" onClick={() => ref.current?.click()}><Upload size={13} className="mr-1.5" />{busy ? "Uploading…" : "Import PDF"}</Button>
        <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
      </div>
      {atts.map((a, i) => (
        <div key={a.id} className="rounded-lg border border-gray-200 p-3" data-testid={`attachment-card-${a.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-[11px] text-gray-500">{a.original_filename}</span>
            <span className="flex items-center gap-0.5">
              <a href={assetUrl(a.url) || "#"} target="_blank" rel="noreferrer" className="rounded p-1 text-gray-400 hover:text-blue-700" data-testid={`open-attachment-${a.id}`}><ExternalLink size={13} /></a>
              {editable && <>
                <button onClick={() => move(i, -1)} className="rounded p-1 text-gray-400 hover:text-gray-900" data-testid={`attachment-up-${a.id}`}><ChevronUp size={13} /></button>
                <button onClick={() => move(i, 1)} className="rounded p-1 text-gray-400 hover:text-gray-900" data-testid={`attachment-down-${a.id}`}><ChevronDown size={13} /></button>
                <button onClick={() => set(atts.filter((x) => x.id !== a.id))} className="rounded p-1 text-gray-400 hover:text-red-600" data-testid={`remove-attachment-${a.id}`}><Trash2 size={13} /></button>
              </>}
            </span>
          </div>
          <div className="mt-2 space-y-2">
            <div><Label className="text-[11px]">Display title</Label><Input disabled={!editable} className="mt-0.5 h-8 text-xs" value={a.title} onChange={(e) => patch(a.id, { title: e.target.value })} placeholder="e.g. Satellite Measurements" data-testid={`attachment-title-${a.id}`} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[11px]">Placement</Label>
                <select disabled={!editable} className="mt-0.5 h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs" value={a.mode} onChange={(e) => patch(a.id, { mode: e.target.value as any })} data-testid={`attachment-mode-${a.id}`}>
                  <option value="appendix">Appendix (end)</option><option value="section">Inline section</option>
                </select></div>
              {a.mode === "section" ? (
                <div><Label className="text-[11px]">Insert after page</Label>
                  <select disabled={!editable} className="mt-0.5 h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs" value={a.position ?? quote.pages.length - 1} onChange={(e) => patch(a.id, { position: Number(e.target.value) })} data-testid={`attachment-position-${a.id}`}>
                    {quote.pages.map((p, idx) => <option key={p.id} value={idx}>{idx + 1}. {p.title}</option>)}
                  </select></div>
              ) : (
                <div><Label className="text-[11px]">Appendix type</Label>
                  <select disabled={!editable} className="mt-0.5 h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs" value={a.appendix_type_id || ""} onChange={(e) => { const t = appendixTypes.find((x) => x.id === e.target.value); patch(a.id, { appendix_type_id: e.target.value || null, title: t ? t.default_title : a.title, description: t ? t.description : a.description }); }} data-testid={`attachment-type-${a.id}`}>
                    <option value="">Custom</option>{appendixTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select></div>
              )}
            </div>
            <div><Label className="text-[11px]">Description (shown on appendix divider)</Label><Input disabled={!editable} className="mt-0.5 h-8 text-xs" value={a.description || ""} onChange={(e) => patch(a.id, { description: e.target.value })} data-testid={`attachment-description-${a.id}`} /></div>
          </div>
        </div>
      ))}
    </div>
  );
}
